import React, { forwardRef, useCallback, useRef, useState, useImperativeHandle } from "react";
import { RCCGrid, RCCHookToast, RCCInput, RCCRefModalProps, RCCCheckBox, RCCTextArea, RCCRangeSlider, RCCAlert, RCCServicesApi } from "react-common-components";
import { useForm } from "react-hook-form";
import AssociateIndicators from "src/components/AssociateIndicators";
import AssociateProjects from "src/components/AssociateProjects";
import InputNumber from "src/components/InputNumber";
import LovUsers from "src/components/Lov/Users";
import Modal from "src/components/Modal";
import ModalSide from "src/components/Modal/styles";
import Select from "src/components/Select";
import Tabs from "src/components/Tabs";
import { useAuth } from "src/context/Auth/context";
import { useConfirmModal } from "src/context/ConfirmModal/context";
import { useLoaderBackdrop } from "src/context/LoaderBackdrop/context";
import { useTranslation } from "src/context/Translations/context";
import { getValidationErrors, standardErrorMessages } from "src/utils/errors";
import stringToNumber from "src/utils/stringToNumber";
import CheckIn from "../components/CheckIn";
import Milestone from "../components/Milestone";
import IOrgChartKeyResultsRef, { CheckInFrequencyType, MeasurementForm, MeasurementType, TabsKRMODAL } from "./interface";
import addOrChangeSchema from "./schemas";


const KeyResultsModal: React.ForwardRefRenderFunction<IOrgChartKeyResultsRef> = (_, ref) => {
	const { currentLanguage, translate } = useTranslation();
	const { user } = useAuth();
	const { setLoading } = useLoaderBackdrop();
	const {addToast} = RCCHookToast();
	const {confirm} = useConfirmModal();

	// Modal Add/Change Key Results
	const onCallbackAddOrChange = useRef<Function | undefined>(undefined);
	const [tabInit, setTabInit] = useState(0);
	const [modalSubtitle, setModalSubtitle] = useState("");
	const [selectedKr, setSelectedKr] = useState<any>({});
	const [isAddKr, setIsAddKr] = useState(true);
	const [showCheckboxFrequencyKr, setShowCheckboxFrequencyKr] = useState(false);
	const [optionsMeasurementType, setOptionsMeasurementType] = useState<{ value: string, label: string}[]>([]);
	const [valueMeasurementType, setValueMeasurementType] = useState<{ label: string; value: string; } | undefined>(undefined);
	const [showMessageMeasurementFormIniciatives, setShowMessageMeasurementFormIniciatives] = useState(selectedKr.okb001Formamedicao ? selectedKr.okb001Formamedicao.toString() === MeasurementForm.INICIATIVES : false);
	const [hiddenTabIndicators, setHiddenTabIndicators] = useState(selectedKr.okb001Formamedicao ? selectedKr.okb001Formamedicao.toString() !== MeasurementForm.INDICATORS : true);
	const [hiddenTabProjects, setHiddenTabProjects] = useState(selectedKr.okb001Formamedicao ? selectedKr.okb001Formamedicao.toString() !== MeasurementForm.PROJECTS : true);
	const [measurementFormEqualsKR, setMeasurementFormEqualsKR] = useState(selectedKr.okb001Formamedicao ? selectedKr.okb001Formamedicao.toString() === MeasurementForm.KEY_RESULTS : false);
	const [milestone, setMilestone] = useState(selectedKr.okb001Formamedicao && selectedKr.okb001Tipomedicao ? selectedKr.okb001Formamedicao.toString() === MeasurementForm.KEY_RESULTS && selectedKr.okb001Tipomedicao.toString() === MeasurementType.MILESTONE : false);
	const [disabled, setDisabled] = useState(false);

	const modalAddOrChangeKr = useRef<RCCRefModalProps>(null);
	const { 
		handleSubmit : handleSubmitAddOrChangeKr, register : registerAddOrChangeKr, control: controlAddOrChangeKr ,
		reset : resetAddOrChangeKr, setError : setErrorAddOrChangeKr, formState: { errors : errorsAddOrChangeKr }, clearErrors: clearErrorsAddOrChangeKr,
		unregister: unregisterAddOrChangeKr, setValue: setValueAddOrChangeKr 
	} = useForm();

	const openModalAddOrChangeKr = async (id?: number, okrParentId?: number, okrParentTitle?: string, onCallback?: Function, tabInitSelected?: TabsKRMODAL, disabledX?: boolean) => {
		setDisabled(disabledX ?? false);
		resetAddOrChangeKr();
		setOptionsMeasurementType([{
			label: translate("Numérico"),
			value: MeasurementType.NUMERIC
		},{
			label: translate("Percentual"),
			value: MeasurementType.PERCENTAGE
		},{
			label: translate("Valor Monetário"),
			value: MeasurementType.MONETARY_VALUE
		},{
			label: translate("Milestone"),
			value: MeasurementType.MILESTONE
		}]);
		if(id){
			setIsAddKr(false);
			try{
				setLoading(true);
				const {data} = await RCCServicesApi.getRequest({
					method: "KR/Select",
					urlParameters: {
						id
					}
				});
				if(data.oktbb002001 && data.oktbb002001.length > 0)
					data.oktbb002001.forEach((okb002: any) => {
						const okb002Changed = okb002;
						okb002Changed.acumulado = 100;
						okb002Changed.id = okb002.okb002Id.toString();
						okb002.oktbb003.forEach((okb003: any) => {
							const okb003Changed = okb003;
							okb003Changed.id = okb003.okb003Id.toString();
						});
					});
				setMeasurementFormEqualsKR(data.okb001Formamedicao.toString() === MeasurementForm.KEY_RESULTS);
				setMilestone(data.okb001Tipomedicao.toString() === MeasurementType.MILESTONE);
				setShowCheckboxFrequencyKr(!data.oktbb009 ? false : data.oktbb009.okb009Tipo !== CheckInFrequencyType.NO_FREQUENCY && data.oktbb009.okb009Tipo !== 0);
				setModalSubtitle(`${translate("Resultado Chave")}: ${data.okb001Titulo}`);
				setHiddenTabIndicators(data.okb001Formamedicao ? data.okb001Formamedicao.toString() !== MeasurementForm.INDICATORS : true);
				setHiddenTabProjects(data.okb001Formamedicao ? data.okb001Formamedicao.toString() !== MeasurementForm.PROJECTS : true);
				setSelectedKr(data);
				setLoading(false);
			}catch(err){
				setLoading(false);
				standardErrorMessages(err, translate("Erro ao selecionar Resultado Chave"), addToast);
			}
		}else{
			setSelectedKr({
				okb001Oka001Id: okrParentId,
				okb001Efa005Id: user.efa005Id
			});
			setModalSubtitle(`OKR: ${ okrParentTitle}` || "");
			setIsAddKr(true);
			setHiddenTabProjects(true);
			setHiddenTabIndicators(true);
			setValueMeasurementType(undefined);
			setShowMessageMeasurementFormIniciatives(false);
			setMeasurementFormEqualsKR(true);
			setMilestone(false);
			setShowCheckboxFrequencyKr(false);
		}
		onCallbackAddOrChange.current = onCallback;
		setTabInit(tabInitSelected || 0);
		modalAddOrChangeKr.current?.openModal();
	};
	const submitAddOrChangeKr = async (data: any) => {
		try{
			clearErrorsAddOrChangeKr();
			modalAddOrChangeKr.current?.setLoadingButton();
			const dataForm = data;

			if(!data.okb001Peso || data.okb001Peso === undefined){
				dataForm.okb001Peso = isAddKr ? 1 : selectedKr.okb001Peso || 1;
			}

			if(data.okb001Tipomedicao === MeasurementType.MILESTONE || 
			   data.okb001Formamedicao === MeasurementForm.INICIATIVES ||
			   data.okb001Formamedicao === MeasurementForm.INDICATORS ||
			   data.okb001Formamedicao === MeasurementForm.PROJECTS
			){
				dataForm.okb001Meta = "100";
				dataForm.okb001Valorinicial = "0";
			}

			if(data.oktbb009.okb009Tipo === CheckInFrequencyType.MONTLHY || data.oktbb009.okb009Tipo === CheckInFrequencyType.WEEKLY){
				if(data.oktbb009.okb009Dom === false &&
				   data.oktbb009.okb009Seg === false &&
				   data.oktbb009.okb009Ter === false &&
				   data.oktbb009.okb009Qua === false &&
				   data.oktbb009.okb009Qui === false &&
				   data.oktbb009.okb009Sex === false &&
				   data.oktbb009.okb009Sab === false
				){
					addToast({
						title: translate("Erro"),
						description: translate("Para períodos com frequência é necessário informar ao menos um dos dias"),
						color: "yellow"
					});
					modalAddOrChangeKr.current?.resetLoadingButton();
					return;
				}
			}

			dataForm.oktbb009.okb009Dom = data.oktbb009.okb009Tipo !== CheckInFrequencyType.NO_FREQUENCY && data.oktbb009.okb009Dom ? "S" : "N";
			dataForm.oktbb009.okb009Seg = data.oktbb009.okb009Tipo !== CheckInFrequencyType.NO_FREQUENCY && data.oktbb009.okb009Seg ? "S" : "N";
			dataForm.oktbb009.okb009Ter = data.oktbb009.okb009Tipo !== CheckInFrequencyType.NO_FREQUENCY && data.oktbb009.okb009Ter ? "S" : "N";
			dataForm.oktbb009.okb009Qua = data.oktbb009.okb009Tipo !== CheckInFrequencyType.NO_FREQUENCY && data.oktbb009.okb009Qua ? "S" : "N";
			dataForm.oktbb009.okb009Qui = data.oktbb009.okb009Tipo !== CheckInFrequencyType.NO_FREQUENCY && data.oktbb009.okb009Qui ? "S" : "N";
			dataForm.oktbb009.okb009Sex = data.oktbb009.okb009Tipo !== CheckInFrequencyType.NO_FREQUENCY && data.oktbb009.okb009Sex ? "S" : "N";
			dataForm.oktbb009.okb009Sab = data.oktbb009.okb009Tipo !== CheckInFrequencyType.NO_FREQUENCY && data.oktbb009.okb009Sab ? "S" : "N";
	
			await addOrChangeSchema(translate).validate(dataForm, {
				abortEarly: false
			});		
			
			let isValid = true;
			if(data.okb001Tipomedicao === MeasurementType.MILESTONE){		
				dataForm.oktbb002001.forEach((okb002: any) => {
					let acumulated = 0;
					if(isValid){
						okb002.oktbb003.forEach((okb003: any) => {
							const okb003Changed = okb003;
							okb003Changed.okb003Percincr = typeof okb003.okb003Percincr === "string" ? stringToNumber(okb003.okb003Percincr, currentLanguage) : okb003.okb003Percincr;
							const sum = okb003Changed.okb003Percincr;
							acumulated += sum || 0;
						});
							
						if(acumulated !== 100){
							addToast({
								title: translate("Erro"),
								description: `${translate("Acumulado dos Itens do milestone deve totalizar")}: 100`,
								color: "yellow"
							});
							isValid = false;
						}
					}
				});
			}
			
			if(!isValid){
				modalAddOrChangeKr.current?.resetLoadingButton();
				return;
			}

			if(isAddKr){
				const request = await RCCServicesApi.postRequest({
					method: "KR/Insert",
					body: {
						...dataForm,
						okb001Oka001Id: selectedKr.okb001Oka001Id,
						okb001Meta: typeof dataForm.okb001Meta === "string" ? stringToNumber(dataForm.okb001Meta, currentLanguage) || 0 : dataForm.okb001Meta,
						okb001Valorinicial: typeof dataForm.okb001Valorinicial === "string" ? stringToNumber(dataForm.okb001Valorinicial, currentLanguage) || 0 : dataForm.okb001Valorinicial
					}
				});

				if(onCallbackAddOrChange.current !== undefined)
					onCallbackAddOrChange.current(request.data);
			}else{
				await RCCServicesApi.putRequest({
					method: "KR/Edit",
					body: {
						...dataForm,
						okb001Id: selectedKr.okb001Id,
						okb001Oka001Id: selectedKr.okb001Oka001Id,
						okb001Meta: typeof dataForm.okb001Meta === "string" ? stringToNumber(dataForm.okb001Meta, currentLanguage) || 0 : dataForm.okb001Meta,
						okb001Valorinicial: typeof dataForm.okb001Valorinicial === "string" ? stringToNumber(dataForm.okb001Valorinicial, currentLanguage) || 0 : dataForm.okb001Valorinicial
					}
				});
				if(onCallbackAddOrChange.current !== undefined)
					onCallbackAddOrChange.current();
			}

			addToast({
				color: "green",
				description: isAddKr ? translate("Resultado Chave adicionado com sucesso") : translate("Resultado Chave alterado com sucesso"),
				title: translate("Sucesso")
			});

			modalAddOrChangeKr.current?.resetLoadingButton();
			modalAddOrChangeKr.current?.closeModal();

		}catch(err){
			modalAddOrChangeKr.current?.resetLoadingButton();
			if(getValidationErrors(err, setErrorAddOrChangeKr)){
				addToast({
					title: translate("Erro"),
					description: translate("Verifique os erros no formulário e tente novamente"),
					color: "yellow"
				});
				return;
			}

			standardErrorMessages(err, isAddKr ? translate("Erro ao adicionar Resultado Chave") : translate("Erro ao alterar Resultado Chave"), addToast);
		}
	};

	const changeSelectFrequencyKr = useCallback((e: { value: string, label: string}) => {
		if(e.value === CheckInFrequencyType.NO_FREQUENCY){
			unregisterAddOrChangeKr("okb009Dom");
			unregisterAddOrChangeKr("okb009Seg");
			unregisterAddOrChangeKr("okb009Ter");
			unregisterAddOrChangeKr("okb009Qua");
			unregisterAddOrChangeKr("okb009Qui");
			unregisterAddOrChangeKr("okb009Sex");
			unregisterAddOrChangeKr("okb009Sab");
			setShowCheckboxFrequencyKr(false);
			return;
		}
		setShowCheckboxFrequencyKr(true);
	}, []);

	const changeMeasurementType = useCallback(() => {
		setValueMeasurementType({
			label: translate("Percentual"),
			value: MeasurementType.PERCENTAGE
		});
		setValueAddOrChangeKr("okb001Tipomedicao", MeasurementType.PERCENTAGE);
		setOptionsMeasurementType([{
			label: translate("Percentual"),
			value: MeasurementType.PERCENTAGE
		}]);
	}, []);

	const changeSelectMeasurementFormKr = useCallback((e: { value: string, label: string}) => {
		setHiddenTabProjects(true);
		setHiddenTabIndicators(true);
		setValueMeasurementType(undefined);
		if(e.value === MeasurementForm.INICIATIVES){
			changeMeasurementType();
			setShowMessageMeasurementFormIniciatives(true);
			unregisterAddOrChangeKr("okb001Valorinicial");
			unregisterAddOrChangeKr("okb001Meta");
			setMeasurementFormEqualsKR(false);
			return;
		}
		setShowMessageMeasurementFormIniciatives(false);
		if(e.value === MeasurementForm.INDICATORS || e.value === MeasurementForm.PROJECTS){
			changeMeasurementType();
			if(e.value === MeasurementForm.PROJECTS)
				setHiddenTabProjects(false);
			if(e.value === MeasurementForm.INDICATORS)
				setHiddenTabIndicators(false);
				
			unregisterAddOrChangeKr("okb001Valorinicial");
			unregisterAddOrChangeKr("okb001Meta");
			setMeasurementFormEqualsKR(false);
			return;
		}
		if(e.value === MeasurementForm.KEY_RESULTS){
			setOptionsMeasurementType([{
				label: translate("Numérico"),
				value: MeasurementType.NUMERIC
			},{
				label: translate("Percentual"),
				value: MeasurementType.PERCENTAGE
			},{
				label: translate("Valor Monetário"),
				value: MeasurementType.MONETARY_VALUE
			},{
				label: translate("Milestone"),
				value: MeasurementType.MILESTONE
			}]);
			setMeasurementFormEqualsKR(true);
		}
	}, []);

	const changeSelectMeasurementTypeKr = useCallback((e: { value: string, label: string}) => {
		if(e.value === MeasurementType.MILESTONE){
			setMilestone(true);
			unregisterAddOrChangeKr("okb001Valorinicial");
			unregisterAddOrChangeKr("okb001Meta");
			return;
		}
		setMilestone(false);
	}, []);

	// Modal Delete Key Results
	const openModalDeleteKr = useCallback((id: number, title: string, onCallback?: Function) => { 
		confirm(
			translate("Exclusão de Resultado Chave"),
			`${translate("Deseja realmente excluir o Resultado Chave")}: ${title}?`,
			async () => {
				await RCCServicesApi.deleteRequest({
					method: "KR/Delete",
					urlParameters: {
						id,
						title
					}
				});
	
				addToast({
					color: "green",
					description: translate("Resultado chave excluído com sucesso"),
					title: translate("Sucesso")
				});

				if(onCallback !== undefined)
					onCallback();
			},
			(err) => {
				standardErrorMessages(err, translate("Erro ao excluir Resultado Chave"), addToast);
			},
			true
		);
	}, []);
	
	useImperativeHandle(ref, () => ({
		openModalDeleteKr, 
		openModalAddOrChangeKr
	}));

	return (
		<>
			{/* Modal Add/Change KR */}
			<Modal
				ref={modalAddOrChangeKr}
				header={{
					title: isAddKr ? translate("Adicionar Resultado Chave") : translate("Alterar Resultado Chave"),
					icon: {
						name: "key"
					},
					subtitle: modalSubtitle && `${modalSubtitle}`
				}}
				footer={{
					showSaveButton: !disabled,
					cancelButtonText: translate("Cancelar"),
					saveButtonText: translate("Salvar"),
					buttonLoadingText: translate("Carregando")
				}}
				onSubmit={handleSubmitAddOrChangeKr(submitAddOrChangeKr)}
				onClose={() => {
					if(onCallbackAddOrChange.current !== undefined)
						onCallbackAddOrChange.current();
				}}
				styleInline={ModalSide}
			>
				<Tabs 
					name="TabOKR"
					tabInit={tabInit}
					options={[{
						label: translate("Geral"),
						content: (
							<RCCGrid
								container
								item
								direction="column"
								alignItems="center"
							>
								<RCCGrid
									container
									item
								>
									<RCCInput 
										register={registerAddOrChangeKr}
										name="okb001Titulo"
										label={translate("Título")}
										maxLength={200}
										defaultValue={selectedKr.okb001Titulo}
										error={errorsAddOrChangeKr.okb001Titulo}
										disabled={disabled}
										required
									/>
								</RCCGrid>
								<RCCGrid
									container
									item
								>
									<RCCTextArea 
										register={registerAddOrChangeKr}
										name="okb001Descricao"
										label={translate("Descrição")}
										maxLength={200}
										defaultValue={selectedKr.okb001Descricao}
										error={errorsAddOrChangeKr.okb001Descricao}			
										disabled={disabled}							
									/>
								</RCCGrid>
								<RCCGrid
									container
									item
								>
									<LovUsers 
										control={controlAddOrChangeKr}
										name="okb001Efa005Id"
										label={translate("Responsável")}
										defaultValue={selectedKr.okb001Efa005Id ? selectedKr.okb001Efa005Id.toString() : undefined}
										error={errorsAddOrChangeKr.okb001Efa005Id}
										disabled={disabled}
										required
									/>
								</RCCGrid>
								<RCCGrid
									container
									item
								>
									<RCCRangeSlider
										control={controlAddOrChangeKr}
										name="okb001Peso"
										color="purple"
										label={translate("Peso")}
										defaultValue={selectedKr.okb001Peso}
										marks={[
											{
												value: 1,
												label: "1"
											},
											{
												value: 100,
												label: "100"
											}
										]}
										min={1}
										max={100}
										disabled={disabled}
										valueLabelDisplay="on"
										styleInline=".MuiSlider-valueLabel > span > span{ color: var(--input-font-color); }"
									/>
								</RCCGrid>
								<RCCGrid
									container
									item
									justifyContent="space-between"
								>
									<RCCGrid
										container
										item
										xs={6}
										sm={5}
									>
										<Select 
											control={controlAddOrChangeKr}
											name="okb001Formamedicao"
											label={translate("Forma de medição")}
											options={[{
												value: "1",
												label: translate("Resultados Chave")
											},{
												value: "2",
												label: translate("Iniciativas")
											},{
												value: "3",
												label: translate("Projetos")
											},{
												value: "4",
												label: translate("Indicadores")
											}]}
											onChangeCapture={(e:any) => changeSelectMeasurementFormKr(e)}
											defaultValue={selectedKr.okb001Formamedicao ? selectedKr.okb001Formamedicao.toString() : "1"}
											error={errorsAddOrChangeKr.okb001Formamedicao}
											disabled={(!isAddKr && (selectedKr.oktbb004 && selectedKr.oktbb004.length > 0)) || 	disabled}
											required
										/>
									</RCCGrid>
									<RCCGrid
										container
										item
										xs={6}
										sm={5}
									>
										<Select 
											control={controlAddOrChangeKr}
											name="okb001Tipomedicao"
											label={translate("Tipo de medição")}
											options={optionsMeasurementType}
											defaultValue={selectedKr.okb001Tipomedicao ? selectedKr.okb001Tipomedicao.toString() : "1"}
											value={valueMeasurementType}
											onChangeCapture={(e:any) => changeSelectMeasurementTypeKr(e)}
											error={errorsAddOrChangeKr.okb001Tipomedicao}
											disabled={(!isAddKr && (selectedKr.oktbb004 && selectedKr.oktbb004.length > 0)) || disabled}
											required
										/>
									</RCCGrid>
								</RCCGrid>
								{measurementFormEqualsKR && (
									<>
										{milestone ? (
											<RCCGrid
												container
												item
											>
												<RCCAlert 
													title={translate("Atenção")} 
													description={translate("Você pode adicionar/editar os milestones desse key result na tab de milestone.")}
												/>
											</RCCGrid>
										) : (
											<RCCGrid
												container
												item
												justifyContent="space-between"
											>
												<RCCGrid
													container
													item
													xs={6}
													sm={5}
												>
													<InputNumber
														name="okb001Valorinicial"
														label={translate("Valor Inicial")}
														control={controlAddOrChangeKr}
														allowNegative
														defaultValue={selectedKr.okb001Valorinicial ? selectedKr.okb001Valorinicial : "0"}
														error={errorsAddOrChangeKr.okb001Valorinicial}
														disabled={disabled}
														required
													/>
												</RCCGrid>
												<RCCGrid
													container
													item
													xs={6}
													sm={5}
												>
													<InputNumber
														name="okb001Meta"
														label={translate("Meta")}
														control={controlAddOrChangeKr}
														allowNegative
														defaultValue={selectedKr.okb001Meta ? selectedKr.okb001Meta : "0"}
														error={errorsAddOrChangeKr.okb001Meta}
														disabled={disabled}
														required
													/>
												</RCCGrid>
											</RCCGrid>
										)}
									</>
								)}
								{isAddKr && showMessageMeasurementFormIniciatives && (
									<RCCGrid
										container
										item
									>
										<RCCAlert
											title={translate("Atenção")} 
											description={translate("Após salvar o Resultados Chave, será liberado a opção para 'Listar Iniciativas' onde você poderá adicionar/editar as iniciativas deste Resultado Chave.")}
										/>
									</RCCGrid>
								)}
							</RCCGrid>
						)
					},{
						label: translate("Milestone"),
						content: (
							<Milestone 
								control={controlAddOrChangeKr}
								error={errorsAddOrChangeKr}
								milestonesDefaultValue={selectedKr.oktbb002001 ? selectedKr.oktbb002001  : []}
								disabled={!!(selectedKr.oktbb004 && selectedKr.oktbb004.length > 0) || disabled}
								register={registerAddOrChangeKr}
								unregister={unregisterAddOrChangeKr}
								setValue={setValueAddOrChangeKr}
								clearErrors={clearErrorsAddOrChangeKr}
							/>
						),
						hidden: !(measurementFormEqualsKR && milestone)
					},{
						label: translate("Frequência"),
						content: (
							<RCCGrid
								container
								item
								direction="column"
								alignItems="center"
							>
								<input 
									type="text"
									value={selectedKr.oktbb009?.okb009Id ? selectedKr.oktbb009?.okb009Id.toString() : "0"}
									{...registerAddOrChangeKr("oktbb009.okb009Id")} 
									style={{ display: "none" }} 
									readOnly
								/>
								<RCCGrid
									container
									item
								>
									<Select 
										control={controlAddOrChangeKr}
										name="oktbb009.okb009Tipo"
										label={translate("Tipo")}
										options={[{
											value: "0",
											label: translate("Sem Frequência")
										},{
											value: "1",
											label: translate("Semanal")
										},{
											value: "2",
											label: translate("Mensal")
										}]}
										defaultValue={selectedKr.oktbb009?.okb009Tipo ? selectedKr.oktbb009?.okb009Tipo.toString() : "0"}
										onChangeCapture={(e:any) => changeSelectFrequencyKr(e)}
										disabled={disabled}
										required
									/>
								</RCCGrid>
								{showCheckboxFrequencyKr && (
									<RCCGrid
										container
										item
										justifyContent="space-between"
									>	
										<RCCCheckBox 
											name="oktbb009.okb009Dom"
											defaultChecked={selectedKr.oktbb009?.okb009Dom === "S"}
											label={translate("Domingo")}
											labelPosition="end"
											register={registerAddOrChangeKr}
											disabled={disabled}
										/>
										<RCCCheckBox 
											name="oktbb009.okb009Seg"
											defaultChecked={selectedKr.oktbb009?.okb009Seg === "S"}
											label={translate("Segunda")}
											labelPosition="end"
											register={registerAddOrChangeKr}
											disabled={disabled}
										/>
										<RCCCheckBox 
											name="oktbb009.okb009Ter"
											defaultChecked={selectedKr.oktbb009?.okb009Ter === "S"}
											label={translate("Terça")}
											labelPosition="end"
											register={registerAddOrChangeKr}
											disabled={disabled}
										/>
										<RCCCheckBox 
											name="oktbb009.okb009Qua"
											defaultChecked={selectedKr.oktbb009?.okb009Qua === "S"}
											label={translate("Quarta")}
											labelPosition="end"
											register={registerAddOrChangeKr}
											disabled={disabled}
										/>
										<RCCCheckBox 
											name="oktbb009.okb009Qui"
											defaultChecked={selectedKr.oktbb009?.okb009Qui === "S"}
											label={translate("Quinta")}
											labelPosition="end"
											register={registerAddOrChangeKr}
											disabled={disabled}
										/>
										<RCCCheckBox 
											name="oktbb009.okb009Sex"
											defaultChecked={selectedKr.oktbb009?.okb009Sex === "S"}
											label={translate("Sexta")}
											labelPosition="end"
											register={registerAddOrChangeKr}
											disabled={disabled}
										/>
										<RCCCheckBox 
											name="oktbb009.okb009Sab"
											defaultChecked={selectedKr.oktbb009?.okb009Sab === "S"}
											label={translate("Sábado")}
											labelPosition="end"
											register={registerAddOrChangeKr}
											disabled={disabled}
										/>
									</RCCGrid>
								)}
							</RCCGrid>
						)
					}, {
						label: "Check In's",
						content: (
							<>
								<CheckIn 
									data={selectedKr.oktbb004} 
									KRgoal={selectedKr.okb001Meta} 
									KRinitialValue={selectedKr.okb001Valorinicial}
									clearCheckInsOnModal={() => setSelectedKr({...selectedKr, oktbb004: []})}
									disabled={disabled}
								/>
							</>
						),
						hidden: isAddKr
					}, {
						label: translate("Indicadores"),
						content: (
							<AssociateIndicators 
								control={controlAddOrChangeKr}
								error={errorsAddOrChangeKr}
								indicatorsDefaultValue={selectedKr.oktbb007 ? selectedKr.oktbb007  : []}
								disabled={!!(selectedKr.oktbb004 && selectedKr.oktbb004.length > 0) || disabled}
								order={false}
								register={registerAddOrChangeKr}
								unregister={unregisterAddOrChangeKr}
								clearErrors={clearErrorsAddOrChangeKr}
								names={{
									nameOfID: "okb007Id",
									nameOfObject: "oktbb007",
									nameOfRangeSlider: "okb007Peso",
									nameOfSelectIndicator: "okb007Iqc001Id"
								}}
							/>
						),
						hidden: hiddenTabIndicators
					}, {
						label: translate("Projetos"),
						content: (
							<AssociateProjects 
								control={controlAddOrChangeKr}
								error={errorsAddOrChangeKr}
								projectsDefaultValue={selectedKr.oktbb008 ? selectedKr.oktbb008  : []}
								disabled={!!(selectedKr.oktbb004 && selectedKr.oktbb004.length > 0) || disabled}
								register={registerAddOrChangeKr}
								clearErrors={clearErrorsAddOrChangeKr}
								names={{
									nameOfID: "okb008Id",
									nameOfObject: "oktbb008",
									nameOfRangeSlider: "okb008Peso",
									nameOfSelectProject: "okb008Pra002Id"
								}}
							/>
						),
						hidden: hiddenTabProjects
					}]}
				/>
			</Modal>
		</>
	);
};

export default forwardRef(KeyResultsModal);
